import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

export type ThemeName = "defaulted" | "smoked" | "spectre" | "reel";

const THEME_COOKIE = "qb_theme";

const themeValues: ThemeName[] = ["defaulted", "smoked", "spectre", "reel"];

const getCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split("=")[1];
};

const parseTheme = (value?: string | null): ThemeName | undefined => {
  if (!value) return undefined;
  const candidate = value.trim().toLowerCase() as ThemeName;
  return themeValues.includes(candidate) ? candidate : undefined;
};

const ThemeContext = createContext<{
  theme: ThemeName;
  setTheme: (theme: ThemeName) => Promise<void>;
  isOverride: boolean;
} | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const overrideTheme = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return parseTheme(params.get("theme"));
  }, [location.search]);

  const [theme, setThemeState] = useState<ThemeName>(() => {
    return overrideTheme ?? parseTheme(getCookie(THEME_COOKIE)) ?? "spectre";
  });

  useEffect(() => {
    if (overrideTheme && overrideTheme !== theme) {
      setThemeState(overrideTheme);
    }
  }, [overrideTheme, theme]);

  useEffect(() => {
    document.body.classList.remove("theme-defaulted", "theme-smoked", "theme-spectre", "theme-reel");
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  const setTheme = async (nextTheme: ThemeName) => {
    setThemeState(nextTheme);

    if (overrideTheme) {
      const params = new URLSearchParams(location.search);
      params.delete("theme");
      navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`, {
        replace: true
      });
    }

    try {
      await fetch("/theme", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme: nextTheme })
      });
    } catch {
      // non-blocking
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isOverride: Boolean(overrideTheme) }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
